<template>
  <div class="reset-password-container">
    <section class="reset-password card">
      <div class="reset-password__header">
        <h1 class="reset-password__headline">{{ $t('myPage.auth.resetPassword.title') }}</h1>
        <div class="reset-password__steps">
          <span>{{ $t('myPage.auth.resetPassword.subTitle') }}</span>
        </div>
      </div>
      <form
        @submit.prevent="submitForm"
        class="reset-password__form"
      >
        <Input
          id="email"
          type="email"
          :label="$t('myPage.auth.resetPassword.placeholder')"
          v-model="email"
          :placeholder="$t('myPage.auth.resetPassword.placeholder')"
          autocomplete="email"
          :aria-label="$t('myPage.auth.resetPassword.placeholder')"
          required
          @input="validateEmail"
          class="reset-password__input"
        />
        <button
          type="submit"
          :disabled="!isEmailValid"
          class="button button--medium button--primary"
          :class="{'disabled': !isEmailValid}"
        >
          {{ $t('myPage.auth.resetPassword.submit') }}
        </button>
        <p v-if="errorMessage" class="reset-password__error">{{ errorMessage }}</p>
        <p v-if="successMessage" class="reset-password__success">{{ successMessage }}</p>
      </form>
    </section>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import Input from '../BaseComponents/Input.vue'
  import Button from '../BaseComponents/Button.vue'
  import { requestPasswordReset } from '@/services/userService'

  export default defineComponent({
    name: 'ResetPassword',
    components: {
      Input,
      Button,
    },
    setup() {
      const { t } = useI18n()
      const email = ref('')
      const isEmailValid = ref(false)
      const errorMessage = ref('')
      const successMessage = ref('')

      const validateEmail = (e: Event) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        isEmailValid.value = emailRegex.test(email.value)
        if (!isEmailValid.value) {
          errorMessage.value = t('myPage.auth.resetPassword.invalidEmail')
        } else {
          errorMessage.value = ''
        }
      }

      const submitForm = async () => {
        try {
          await requestPasswordReset(email.value)
          successMessage.value = t('myPage.auth.resetPassword.successMessage')
          errorMessage.value = ''
        } catch (error: any) {
          console.error('Reset password error:', error)
          if (error.response?.status === 404) {
            errorMessage.value = t('myPage.auth.resetPassword.userNotFound')
          } else if (error instanceof Error && error.message) {
            errorMessage.value = error.message
          } else {
            errorMessage.value = t('myPage.auth.resetPassword.errorMessage')
          }
          successMessage.value = ''
        }
      }

      return {
        email,
        isEmailValid,
        errorMessage,
        successMessage,
        validateEmail,
        submitForm,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .reset-password-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-right: 16px;
    margin-left: 16px;
    padding: rem(16) 16px;
  }

  .reset-password {
    margin: rem(16) 16px;
    padding: rem(16) 16px;
    width: 100%;
    max-width: 543px;
    color: $clr-primary;
    background-color: $clr-white;
    box-shadow: $box-shadow-card;
    border-radius: 8px;

    @media screen and (min-width: $breakpoint-xs) {
      padding: rem(30) 30px;
    }

    &__header {
      display: flex;
      flex-direction: column-reverse;
      gap: 32px;
    }

    &__steps {
      @include title-small;
      display: flex;
      justify-content: space-between;
      gap: 24px;
      color: $clr-text-disable;
    }

    &__headline {
      @include headline-medium;
      margin-top: 0;
      text-align: left;
    }

    p {
      @include body-large;
      /* margin: rem(30) 0; */
      text-align: left;
    }

    &__submit {
      margin-bottom: 20px;
    }

    &__button {
      margin-top: rem(16);
      width: 100%;
    }

    &__form {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__input {
      width: 100%;
    }

    &__error {
      color: $clr-error;
      text-align: center;
    }

    &__success {
      color: $clr-success;
      text-align: center;
    }
  }
</style>
